import React from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Chip,
  Typography,
  CardActionArea,
  IconButton,
  Avatar as MUIAvatar,
  alpha,
} from '@mui/material';
import DifficultyLevelChip from './difficulty-level-chip';
import { LockRounded, PlayArrowRounded } from '@mui/icons-material';
import { ICourseData } from '../../types/course-data';
import Avatar from './avatar';
import { SkilllevelsType } from '../../types/levels';
import { EventBus } from '@/shared/utils';
import { CatalogEvents } from '../../events';
import { getCardColors } from '../../utils/common';
import { adaptCourseTypeText } from 'src/shared/utils/adapt-course-type-text';
import { LazyImage } from 'src/shared/ui/lazy-image';

// Add variant prop to control display mode
export type CourseCardVariant = 'catalog' | 'dashboard';

export interface CourseCardProps extends ICourseData {
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  onIsLockedClick?: () => void;
  eventBus: EventBus<CatalogEvents>;
  colorScheme: any;
  variant?: CourseCardVariant;
  onFavoriteChange?: (courseId: string, favouriteId: string | undefined) => void;
  isEnrichingData?: boolean;
}

export const CourseCard: React.FC<CourseCardProps> = ({
  thumbnailCard,
  videoCount,
  coursePercentage,
  title,
  genres = [],
  instructors,
  courseType,
  level,
  highlighted,
  isCourseStarted,
  isLocked,
  isSlider,
  colorScheme,
  upComingEvent,
  onClick,
  onIsLockedClick,
  variant = 'catalog', // Default to catalog variant
}) => {
  // Determine if we should show the play button based on variant and course state
  const showPlayButton = variant === 'dashboard' && isCourseStarted;

  // Determine if we should show course type badge
  const showCourseType = variant === 'catalog' || !isCourseStarted;

  const isMasterclass = courseType?.toLowerCase() === 'masterclass';

  // Determine if we should show video count badge
  const showVideoCount = (variant === 'catalog' || !isCourseStarted) && !isMasterclass;

  // Determine if we should show percentage badge (only in dashboard when course is started)
  const showPercentageBadge =
    variant === 'dashboard' && isCourseStarted && coursePercentage !== undefined;

  // Determine if we should show level/genre info
  const showLevelGenreInfo = variant === 'catalog' || !isCourseStarted;

  // Determine if we should show teacher profile
  const showTeacherProfile = variant === 'catalog' || !isCourseStarted;

  // Determine if we should show progress bar
  const showProgressBar = isCourseStarted;

  return (
    <>
      <Card
        onClick={isLocked ? onIsLockedClick : onClick}
        className={`${isSlider ? 'course-card group overflow-hidden w-full' : 'w-full max-w-[318px] md:max-w-full mx-auto'} h-[334px] md:h-[352px] relative`}
        sx={{
          boxShadow: 'none',
          backgroundColor: colorScheme?.card.bg,
          color: colorScheme?.card.text,
        }}
      >
        {isLocked && (
          <div
            className="absolute z-10 flex flex-col items-center justify-center w-full h-full pointer-events-none inset"
            style={{
              backgroundColor: !highlighted
                ? 'rgba(84,86,104,0.7)'
                : alpha(colorScheme?.card.bg || '#ffffff', 0.7),
              backdropFilter: 'blur(2px)',
            }}
          >
            <div className="-translate-y-20 text-center">
              <LockRounded className="h-12" />
              <Typography variant="s1" component="p">
                Abbonati per guardare
              </Typography>
            </div>
          </div>
        )}
        <CardActionArea className="flex flex-col justify-start w-full h-full rounded">
          <div className="relative w-full">
            <LazyImage
              src={thumbnailCard || ''}
              alt={title}
              placeholder="/assets/images/card-placeholder.png"
              className="object-cover relative w-full h-[180px] md:h-[196px]"
            />
            {showCourseType && courseType && (
              <Chip
                label={
                  <Typography variant="button-s" className="capitalize">
                    {adaptCourseTypeText(courseType)}
                  </Typography>
                }
                size="small"
                variant="filled"
                className="absolute top-4 left-4 min-h-[24px]"
                sx={{
                  backgroundColor: colorScheme?.courseTypeTag.bg,
                  color: colorScheme?.courseTypeTag.text,
                }}
              />
            )}
            {!!upComingEvent && (
              <Chip
                avatar={<MUIAvatar src="/assets/images/calendar.png" className="w-6 h-6 mr-0" />}
                label={
                  <Typography variant="button-s" component="p">
                    {upComingEvent}
                  </Typography>
                }
                size="medium"
                className="absolute top-4 right-4 bg-neutral-main"
                sx={{ padding: '0', height: '30px' }}
              />
            )}
            {showVideoCount && videoCount !== undefined && (
              <Chip
                label={
                  <Typography variant="button-s">
                    {`${videoCount} lezion${videoCount !== 1 ? 'i' : 'e'}`}
                  </Typography>
                }
                size="medium"
                className="absolute top-4 right-4 bg-neutral-main"
                avatar={<MUIAvatar src="/assets/images/play-arrow-filled.png" className="mr-0" />}
              />
            )}
            {showPercentageBadge && (
              <Chip
                label={`${coursePercentage}%`}
                size="medium"
                className="absolute top-4 right-4 bg-neutral-main"
                sx={{ padding: '0', height: '30px' }}
              />
            )}
          </div>
          <CardContent
            className="flex flex-col justify-between flex-1 w-full pb-4 space-y-2"
            style={{
              color: colorScheme?.card.text,
            }}
          >
            <div className="space-y-2">
              <div className="flex items-center gap-4">
                <Typography
                  variant="h5"
                  color={colorScheme?.card.text}
                  className="gap-2.5 self-stretch w-full my-auto text-left line-clamp-2"
                >
                  {title}
                </Typography>
                {showPlayButton && (
                  <IconButton
                    size="medium"
                    color="primary"
                    className="button-icon-contained button-icon-primary h-[44px] px-2"
                    sx={{ width: '44px !important' }}
                  >
                    <PlayArrowRounded />
                  </IconButton>
                )}
              </div>
              {showLevelGenreInfo && (
                <div className="flex flex-wrap gap-1">
                  <DifficultyLevelChip
                    courseType={courseType}
                    level={level as SkilllevelsType}
                    highlighted={highlighted}
                  />
                  {Array.isArray(genres) &&
                    genres.map((genre, index) => (
                      <Chip
                        key={index}
                        label={
                          <Typography
                            variant="button-s"
                            sx={{ color: getCardColors(courseType, highlighted)?.genreChipColor }}
                          >
                            {genre}
                          </Typography>
                        }
                        size="small"
                        color="default"
                        variant="outlined"
                        sx={{
                          borderColor: colorScheme?.musicalGenre.border,
                        }}
                      />
                    ))}
                </div>
              )}
            </div>

            <div className="mt-2 md:mt-4">
              {showTeacherProfile && (
                <div className="flex items-center">
                  <Avatar
                    instructors={instructors}
                    courseType={courseType}
                    borderColor={colorScheme?.card.bg}
                  />
                </div>
              )}

              {/* {showProgressBar && (
                <div className="py-2 mt-2">
                  <LinearProgress
                    className="h-[8px] bg-neutral-main"
                    variant="determinate"
                    value={coursePercentage || 0}
                    sx={{ backgroundColor: '#E2E3E9' }}
                  />
                </div>
              )} */}
            </div>
          </CardContent>
        </CardActionArea>
      </Card>
    </>
  );
};
